import { useRef, useState } from "react";
import { FolderCog, MoreHorizontal } from "lucide-react";
import { api, ApiError, type Folder } from "../api";
import { fehlerText, toast, useDialog } from "./verwaltung";
import { KontextMenue, type MenuEintrag } from "./KontextMenue";
import { Select } from "./Select";
import { RegelnAnwenden } from "./RegelnAnwenden";

/** Anzeigename wie in der Seitenleiste: Systemordner deutsch, sonst der Pfad mit „/". */
export function ordnerAnzeige(f: Folder): string {
  const namen: Record<string, string> = {
    "\\Inbox": "Posteingang", "\\Sent": "Gesendet", "\\Drafts": "Entwürfe",
    "\\Archive": "Archiv", "\\Junk": "Unerwünscht", "\\Trash": "Papierkorb",
  };
  if (f.special_use && namen[f.special_use]) return namen[f.special_use]!;
  if (f.path.toUpperCase() === "INBOX") return "Posteingang";
  return f.delimiter ? f.path.split(f.delimiter).join(" / ") : f.path;
}

const istSystem = (f: Folder) => Boolean(f.special_use) || f.path.toUpperCase() === "INBOX";

type Dialog =
  | { art: "anlegen" }
  | { art: "umbenennen" }
  | { art: "verschieben"; ziel?: string }
  | { art: "einhaengen"; elternId: number | null }
  | { art: "loeschen" };

const OBERSTE = "__oberste__";

interface Props {
  folder: Folder;
  folders: Folder[];
  /** `icon` für die Seitenleiste, `button` für den Kopf der Liste. */
  variant?: "icon" | "button";
  onChanged: () => void;
  /** Nach dem Löschen: Ansicht verlassen, der Ordner existiert nicht mehr. */
  onDeleted?: () => void;
}

/**
 * Aktionen für einen Ordner: Unterordner anlegen, umbenennen, Inhalt
 * verschieben, löschen. Löschen mit Inhalt geht nur mit einem Ziel für die
 * Mails — endgültig weg ist hier nie etwas.
 */
export function OrdnerAktionen({ folder, folders, variant = "icon", onChanged, onDeleted }: Props) {
  const knopf = useRef<HTMLButtonElement | null>(null);
  const [menue, setMenue] = useState<{ x: number; y: number } | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [analyse, setAnalyse] = useState(false);

  const ziele = folders.filter((f) => f.id !== folder.id).sort((a, b) => ordnerAnzeige(a).localeCompare(ordnerAnzeige(b), "de"));
  // Als neuer Elternordner taugt alles außer dem Ordner selbst und seinen Unterordnern.
  const eltern = ziele.filter((f) =>
    !(folder.delimiter && f.path.startsWith(folder.path + folder.delimiter)) &&
    !["\\Sent", "\\Drafts", "\\Junk", "\\Trash"].includes(f.special_use ?? "") &&
    f.path !== folder.path.split(folder.delimiter ?? "\u0000").slice(0, -1).join(folder.delimiter ?? ""));

  const oeffnen = () => {
    const r = knopf.current?.getBoundingClientRect();
    if (r) setMenue({ x: variant === "icon" ? r.right : r.left, y: r.bottom + 4 });
  };

  const eintraege: MenuEintrag[] = [
    { id: "analysieren", label: "Neu analysieren und zuordnen …", aktion: () => setAnalyse(true) },
    { id: "anlegen", label: "Unterordner anlegen …", trenner: true, aktion: () => setDialog({ art: "anlegen" }) },
    ...(!istSystem(folder) ? [
      { id: "umbenennen", label: "Umbenennen …", aktion: () => setDialog({ art: "umbenennen" }) },
      {
        id: "einhaengen",
        label: "Ordner verschieben unter",
        unterliste: [
          ...(folder.delimiter && folder.path.includes(folder.delimiter) ? [{ id: OBERSTE, label: "Oberste Ebene" }] : []),
          ...eltern.map((f) => ({ id: String(f.id), label: ordnerAnzeige(f) })),
        ],
      },
    ] : []),
    {
      id: "verschieben",
      label: "Inhalt verschieben nach",
      trenner: true,
      unterliste: ziele.map((f) => ({ id: f.path, label: ordnerAnzeige(f) })),
    },
    ...(!istSystem(folder) ? [{ id: "loeschen", label: "Ordner löschen …", trenner: true, gefahr: true, aktion: () => setDialog({ art: "loeschen" }) }] : []),
  ];

  return (
    <>
      {variant === "icon" ? (
        <button
          ref={knopf}
          type="button"
          className="nav-menue"
          aria-label={`Aktionen für ${ordnerAnzeige(folder)}`}
          title="Ordneraktionen"
          onClick={(e) => { e.stopPropagation(); oeffnen(); }}
        >
          <MoreHorizontal aria-hidden="true" />
        </button>
      ) : (
        <button ref={knopf} type="button" className="btn quiet ordner-knopf" onClick={oeffnen} title="Ordneraktionen" aria-label="Ordneraktionen">
          <FolderCog aria-hidden="true" /> <span className="nur-breit">Ordner</span>
        </button>
      )}

      {menue && (
        <KontextMenue
          x={menue.x}
          y={menue.y}
          eintraege={eintraege}
          onClose={() => setMenue(null)}
          onUnterwahl={(id, wert) =>
            id === "einhaengen"
              ? setDialog({ art: "einhaengen", elternId: wert === OBERSTE ? null : Number(wert) })
              : setDialog({ art: "verschieben", ziel: wert })}
        />
      )}

      {analyse && (
        <RegelnAnwenden folders={folders} ordner={folder} onClose={() => setAnalyse(false)} onDone={onChanged} />
      )}

      {dialog && (
        <OrdnerDialog
          folder={folder}
          ziele={ziele}
          dialog={dialog}
          onClose={() => setDialog(null)}
          onDone={(geloescht, text) => { setDialog(null); toast.erfolg(text); onChanged(); if (geloescht) onDeleted?.(); }}
        />
      )}
    </>
  );
}

function OrdnerDialog({ folder, ziele, dialog, onClose, onDone }: {
  folder: Folder;
  ziele: Folder[];
  dialog: Dialog;
  onClose: () => void;
  onDone: (geloescht: boolean, meldung: string) => void;
}) {
  const box = useRef<HTMLFormElement | null>(null);
  const [name, setName] = useState(dialog.art === "umbenennen" ? folder.name : "");
  const [ziel, setZiel] = useState(dialog.art === "verschieben" ? dialog.ziel ?? "" : "");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Der lokale Zähler kann hinter dem Server herhinken; meldet der Server
  // beim Löschen noch Mails (409), fragt der Dialog nach einem Ziel.
  const [zielNoetig, setZielNoetig] = useState(folder.total > 0);
  const anzeige = ordnerAnzeige(folder);
  useDialog(box, onClose, !busy);

  const titel = {
    anlegen: `Unterordner in „${anzeige}" anlegen`,
    umbenennen: `„${anzeige}" umbenennen`,
    verschieben: `Inhalt von „${anzeige}" verschieben`,
    einhaengen: `„${anzeige}" verschieben`,
    loeschen: `„${anzeige}" löschen`,
  }[dialog.art];
  const neuerElternordner = dialog.art === "einhaengen" && dialog.elternId !== null
    ? ziele.find((f) => f.id === dialog.elternId) : undefined;
  const zielName = (pfad: string) => { const f = ziele.find((x) => x.path === pfad); return f ? ordnerAnzeige(f) : pfad; };
  const teile = name.split("/").map((t) => t.trim()).filter(Boolean);

  const ausfuehren = async () => {
    setBusy(true);
    setFehler(null);
    try {
      if (dialog.art === "anlegen") {
        const basis = folder.delimiter ? folder.path.split(folder.delimiter) : [folder.path];
        await api.ordnerAnlegen([...basis, ...teile], folder.account_id);
        onDone(false, `Ordner „${teile.join(" / ")}" in „${anzeige}" angelegt.`);
      } else if (dialog.art === "umbenennen") {
        await api.ordnerUmbenennen(folder.id, name.trim());
        onDone(false, `„${anzeige}" heißt jetzt „${name.trim()}".`);
      } else if (dialog.art === "verschieben") {
        const r = await api.ordnerInhaltVerschieben(folder.id, ziel);
        onDone(false, `${r.anzahl.toLocaleString("de-DE")} ${r.anzahl === 1 ? "Mail" : "Mails"} nach „${zielName(ziel)}" verschoben.`);
      } else if (dialog.art === "einhaengen") {
        await api.ordnerEinhaengen(folder.id, dialog.elternId);
        onDone(false, neuerElternordner ? `„${anzeige}" liegt jetzt unter „${ordnerAnzeige(neuerElternordner)}".` : `„${anzeige}" liegt jetzt auf der obersten Ebene.`);
      } else {
        const r = await api.ordnerLoeschen(folder.id, ziel || undefined);
        onDone(true, r?.verschoben
          ? `„${anzeige}" gelöscht, ${r.verschoben.toLocaleString("de-DE")} ${r.verschoben === 1 ? "Mail" : "Mails"} nach „${zielName(ziel)}" verschoben.`
          : `„${anzeige}" gelöscht.`);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && dialog.art === "loeschen" && /Mails/.test(e.message)) setZielNoetig(true);
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };

  const bereit =
    dialog.art === "anlegen" ? teile.length > 0
    : dialog.art === "umbenennen" ? name.trim().length > 0 && name.trim() !== folder.name && !/[/\\]/.test(name)
    : dialog.art === "verschieben" ? ziel !== ""
    : dialog.art === "einhaengen" ? true
    : !zielNoetig || ziel !== "";

  const zielOptionen = ziele.map((f) => ({ value: f.path, label: ordnerAnzeige(f), hint: f.total ? f.total.toLocaleString("de-DE") : undefined }));

  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form
        ref={box}
        className="sheet"
        style={{ width: "min(480px, 100%)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ordner-dialog-titel"
        onSubmit={(e) => { e.preventDefault(); if (bereit && !busy) void ausfuehren(); }}
      >
        <div className="sheet-head"><span id="ordner-dialog-titel">{titel}</span></div>
        <div className="sheet-body">
          {(dialog.art === "anlegen" || dialog.art === "umbenennen") && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="ordnername">{dialog.art === "anlegen" ? "Name des neuen Ordners" : "Neuer Name"}</label>
              <input id="ordnername" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                placeholder={dialog.art === "anlegen" ? "z. B. Neuer Kunde" : undefined} />
              {dialog.art === "anlegen" && <p className="hint">Mit „/" auch mehrere Ebenen auf einmal, etwa „Projekte/2026".</p>}
              {dialog.art === "umbenennen" && (
                /[/\\]/.test(name)
                  ? <p className="error" style={{ margin: "6px 0 0" }}>Schrägstriche sind im Namen nicht erlaubt. Zum Verschieben „Ordner verschieben unter" nutzen.</p>
                  : <p className="hint">Unterordner ziehen mit, und Regeln, die hierher verschieben, werden automatisch angepasst.</p>
              )}
            </div>
          )}

          {dialog.art === "verschieben" && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label>
                {folder.total > 0 ? `Alle ${folder.total.toLocaleString("de-DE")} Mails verschieben nach` : "Alle Mails verschieben nach"}
              </label>
              <Select value={ziel} onChange={setZiel} placeholder="Zielordner wählen …" options={zielOptionen} aria-label="Zielordner" />
              <p className="hint">Die Einordnungen der Mails bleiben erhalten. Der Ordner selbst bleibt bestehen.</p>
            </div>
          )}

          {dialog.art === "einhaengen" && (
            <p style={{ margin: 0 }}>
              „{anzeige}" wandert samt allen Unterordnern und Mails{" "}
              {neuerElternordner ? <>unter <strong>{ordnerAnzeige(neuerElternordner)}</strong></> : <>auf die <strong>oberste Ebene</strong></>}.
              Regeln, die hierher verschieben, werden automatisch angepasst.
            </p>
          )}

          {dialog.art === "loeschen" && (
            <>
              <p style={{ margin: "0 0 12px" }}>
                {zielNoetig
                  ? <>Im Ordner liegen {folder.total > 0 ? <strong>{folder.total.toLocaleString("de-DE")} Mails</strong> : "noch Mails"}.
                      Sie werden vorher in einen anderen Ordner verschoben — gelöscht wird dabei keine Mail.</>
                  : <>Der Ordner ist leer und wird auf dem Server entfernt.</>}
              </p>
              {zielNoetig && (
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Mails verschieben nach</label>
                  <Select value={ziel} onChange={setZiel} placeholder="Zielordner wählen …" options={zielOptionen} aria-label="Zielordner" />
                </div>
              )}
            </>
          )}
          {fehler && (
            <div className="v-meldung" data-art="fehler" role="alert" style={{ margin: "14px 0 0" }}>
              <span>{fehler}</span>
            </div>
          )}
        </div>
        <div className="sheet-foot">
          <div className="spacer" />
          <button type="button" className="btn quiet" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="submit" className={dialog.art === "loeschen" ? "btn danger-solid" : "btn primary"} disabled={!bereit || busy}>
            {busy ? "Einen Moment …" : {
              anlegen: "Anlegen",
              umbenennen: "Umbenennen",
              verschieben: "Verschieben",
              einhaengen: "Verschieben",
              loeschen: zielNoetig ? "Verschieben und löschen" : "Ordner löschen",
            }[dialog.art]}
          </button>
        </div>
      </form>
    </div>
  );
}
